import { PersistentStorage } from "../storage/PersistentStorage";
import {
  OfflineQueueItem,
  OfflineQueueState,
  OfflineQueueStatus,
  ItemStatusMutationPayload,
  OfflineMutationPayload,
  OfflineEntityMetadata,
} from "./types";

const QUEUE_STORAGE_KEY = "offline:queue";
const QUEUE_VERSION = 1;

type QueueListener = (state: OfflineQueueState) => void;

export class OfflineQueueManager {
  private listeners = new Set<QueueListener>();
  private cache: OfflineQueueState | null = null;

  constructor(
    private readonly storage: PersistentStorage,
    private readonly storageKey: string = QUEUE_STORAGE_KEY
  ) {}

  subscribe(listener: QueueListener): () => void {
    this.listeners.add(listener);
    void this.getState().then(listener).catch(console.error);
    return () => {
      this.listeners.delete(listener);
    };
  }

  async getState(): Promise<OfflineQueueState> {
    if (this.cache) {
      return this.cache;
    }
    const stored = await this.storage.getItem<OfflineQueueState>(this.storageKey);
    this.cache = migrateState(stored);
    return this.cache;
  }

  async enqueueItemStatusMutation(
    payload: ItemStatusMutationPayload
  ): Promise<OfflineQueueItem> {
    const state = await this.getState();
    const item: OfflineQueueItem = {
      id: generateQueueId(),
      type: "update-item-status",
      payload,
      metadata: {
        status: "pending",
        createdAt: Date.now(),
        updatedAt: Date.now(),
        attempts: 0,
      },
    };
    state.items.push(item);
    state.entityMetadata[getEntityKey(payload)] = buildMetadata("pending");
    await this.persist(state);
    return item;
  }

  async markItemSyncing(id: string): Promise<OfflineQueueItem | null> {
    const state = await this.getState();
    const target = state.items.find((item) => item.id === id);
    if (!target) return null;
    target.metadata.status = "syncing";
    target.metadata.attempts += 1;
    target.metadata.updatedAt = Date.now();
    state.entityMetadata[getEntityKey(target.payload)] = buildMetadata("syncing");
    await this.persist(state);
    return target;
  }

  async markItemSynced(
    id: string,
    lastSyncAt: number = Date.now()
  ): Promise<void> {
    const state = await this.getState();
    const index = state.items.findIndex((item) => item.id === id);
    if (index === -1) return;
    const [item] = state.items.splice(index, 1);
    item.metadata.lastSyncAt = lastSyncAt;
    state.entityMetadata[getEntityKey(item.payload)] = buildMetadata("synced", {
      lastSyncAt,
      lastError: undefined,
    });
    await this.persist(state);
  }

  async markItemFailed(
    id: string,
    errorMessage: string
  ): Promise<void> {
    const state = await this.getState();
    const target = state.items.find((item) => item.id === id);
    if (!target) return;
    target.metadata.status = "error";
    target.metadata.updatedAt = Date.now();
    target.metadata.lastError = errorMessage;
    state.entityMetadata[getEntityKey(target.payload)] = buildMetadata("error", {
      lastError: errorMessage,
      lastSyncAt: target.metadata.lastSyncAt,
    });
    await this.persist(state);
  }

  async markItemPending(id: string, errorMessage?: string): Promise<void> {
    const state = await this.getState();
    const target = state.items.find((item) => item.id === id);
    if (!target) return;
    target.metadata.status = "pending";
    target.metadata.updatedAt = Date.now();
    target.metadata.lastError = errorMessage;
    state.entityMetadata[getEntityKey(target.payload)] = buildMetadata("pending", {
      lastError: errorMessage,
      lastSyncAt: target.metadata.lastSyncAt,
    });
    await this.persist(state);
  }

  async setPaused(paused: boolean, reason?: string): Promise<void> {
    const state = await this.getState();
    state.paused = paused;
    state.pausedReason = paused ? reason : undefined;
    await this.persist(state);
  }

  async clear(): Promise<void> {
    const empty = createInitialState();
    await this.persist(empty);
  }

  private async persist(state: OfflineQueueState): Promise<void> {
    state.version = QUEUE_VERSION;
    this.cache = state;
    await this.storage.setItem(this.storageKey, state);
    this.listeners.forEach((listener) => {
      try {
        listener(state);
      } catch (error) {
        console.error("[OfflineQueueManager] listener error", error);
      }
    });
  }
}

const buildMetadata = (
  status: OfflineQueueStatus,
  overrides: Partial<OfflineEntityMetadata> = {}
): OfflineEntityMetadata => ({
  status,
  lastSyncAt: overrides.lastSyncAt,
  lastError: overrides.lastError,
});

const migrateState = (state: OfflineQueueState | null | undefined): OfflineQueueState => {
  if (!state) {
    return createInitialState();
  }
  if (!state.entityMetadata) {
    state.entityMetadata = {};
  }
  state.version = QUEUE_VERSION;
  return state;
};

const createInitialState = (): OfflineQueueState => ({
  version: QUEUE_VERSION,
  items: [],
  paused: false,
  entityMetadata: {},
});

const getEntityKey = (payload: OfflineMutationPayload): string => {
  if (payload.entity === "item") {
    return `${payload.entity}:${payload.itemId}`;
  }
  return `${payload.entity}:${payload.orderId}`;
};

const generateQueueId = (): string =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
